/**
 * Products admin API — Settings → Product & Services.
 * Covers: product groups CRUD, products search/CRUD, product-group VAT rates.
 *
 * Endpoints confirmed live:
 *  GET  /product-groups                → { results: ProductGroupRow[] }
 *  POST /product-groups                → create
 *  PUT  /product-groups/:id            → update
 *  DELETE /product-groups/:id          → delete
 *
 *  GET  /products/search               → { results: ProductSearchRow[], pagination }
 *  POST /products                      → create
 *  PUT  /products/:id                  → update
 *  DELETE /products/:id                → delete
 *
 *  GET  /product-groups/vat-rates      → { results: ProductGroupWithVatRates[] }
 *  POST /product-groups/:code/vat-rates  → create VAT rate
 *  PUT  /product-groups/:code/vat-rates/:id → update
 *  DELETE /product-groups/:code/vat-rates/:id → delete
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import type { ResponseArray } from '@/types/shared';

// ─── Product Groups ───────────────────────────────────────────────────────────

export interface ProductGroupRow {
  product_group_id: number;
  product_group_name: string;
  product_group_description: string | null;
  product_group_type: string | null;
  product_group_hidden: boolean;
  product_group_code: string;
  product_group_created_at: string | null;
  product_group_updated_at: string | null;
  api_template_id: number | null;
  service_price_type: { service_price_type: string; valid: boolean } | null;
  product_percentage: number | null;
  deleted_at: string | null;
}

export interface CreateProductGroupPayload {
  product_group_name: string;
  product_group_code: string;
  product_group_type?: string;
  product_group_hidden?: boolean;
}

export interface UpdateProductGroupPayload {
  product_group_name?: string;
  product_group_code?: string;
  product_group_type?: string;
  product_group_hidden?: boolean;
}

export const PRODUCT_GROUPS_QK = ['admin', 'product-groups'] as const;

export function useAdminProductGroups() {
  return useQuery({
    queryKey: PRODUCT_GROUPS_QK,
    queryFn: async (): Promise<ProductGroupRow[]> => {
      const res = await dataCentralApi.get<ResponseArray<ProductGroupRow>>(
        '/product-groups?page=1&page_size=200',
      );
      return res.data.results ?? [];
    },
    staleTime: 30_000,
  });
}

export function useCreateProductGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateProductGroupPayload) =>
      dataCentralApi.post('/product-groups', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: PRODUCT_GROUPS_QK }),
  });
}

export function useUpdateProductGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: UpdateProductGroupPayload }) =>
      dataCentralApi.put(`/product-groups/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: PRODUCT_GROUPS_QK }),
  });
}

export function useDeleteProductGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => dataCentralApi.delete(`/product-groups/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: PRODUCT_GROUPS_QK }),
  });
}

// ─── Products ─────────────────────────────────────────────────────────────────

export interface ProductSearchRow {
  product_id: number;
  product_name: string;
  product_code: string | null;
  product_hidden: boolean;
  product_description: string | null;
  product_group_id: number;
  product_group_name: string;
  template_id: number | null;
  is_product_addon: boolean;
  product_price_model: string | null;
  current_price: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface ProductSearchParams {
  page?: number;
  page_size?: number;
  q?: string;
  product_group_id?: number;
  is_addon?: string;
}

export const PRODUCTS_QK = ['admin', 'products'] as const;

export function useAdminProducts(params: ProductSearchParams) {
  return useQuery({
    queryKey: [...PRODUCTS_QK, params],
    queryFn: async () => {
      const query = qs.stringify({
        page: params.page ?? 1,
        page_size: params.page_size ?? 20,
        ...(params.q ? { q: params.q } : {}),
        ...(params.product_group_id ? { product_group_id: params.product_group_id } : {}),
        ...(params.is_addon !== undefined ? { is_addon: params.is_addon } : {}),
      });
      const res = await dataCentralApi.get<ResponseArray<ProductSearchRow>>(
        `/products/search?${query}`,
      );
      return res.data;
    },
  });
}

export interface CreateProductPayload {
  product_name: string;
  product_code?: string;
  product_hidden?: boolean;
  product_description?: string;
  product_group_id?: number;
  template_id?: number;
  is_product_addon?: boolean;
}

export interface UpdateProductPayload {
  product_name?: string;
  product_code?: string;
  product_hidden?: boolean;
  product_description?: string;
  product_group_id?: number;
  template_id?: number;
  is_product_addon?: boolean;
  product_price_model?: string;
}

export function useDeleteProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => dataCentralApi.delete(`/products/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: PRODUCTS_QK }),
  });
}

// ─── VAT Rates ────────────────────────────────────────────────────────────────

export interface VatRate {
  id: number;
  product_group_code: string;
  tax_rate: number;
  effective_from: string;
  effective_to: string | null;
  description: string | null;
  created_at: string;
  updated_at: string;
  product_group_name: string;
}

export interface ProductGroupWithVatRates {
  product_group_code: string;
  product_group_name: string;
  vat_rates: VatRate[];
}

export const VAT_RATES_QK = ['admin', 'product-group-vat-rates'] as const;

export function useAdminVatRates() {
  return useQuery({
    queryKey: VAT_RATES_QK,
    queryFn: async (): Promise<ProductGroupWithVatRates[]> => {
      const res = await dataCentralApi.get<ResponseArray<ProductGroupWithVatRates>>(
        '/product-groups/vat-rates',
      );
      return res.data.results ?? [];
    },
    staleTime: 30_000,
  });
}

export interface CreateVatRatePayload {
  tax_rate: number;
  effective_from: string;
  effective_to?: string | null;
  description?: string;
}

export interface UpdateVatRatePayload {
  tax_rate?: number;
  effective_from?: string;
  effective_to?: string | null;
  description?: string;
}

export function useCreateVatRate() {
  const qc = useQueryClient();
  return useMutation({
    // groupId = product_group_id (numeric, from product-groups list). Route: POST /product-groups/:groupID/vat-rates
    mutationFn: ({ groupId, payload }: { groupId: number; payload: CreateVatRatePayload }) =>
      dataCentralApi.post(`/product-groups/${groupId}/vat-rates`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: VAT_RATES_QK }),
  });
}

export function useUpdateVatRate() {
  const qc = useQueryClient();
  return useMutation({
    // groupId = product_group_id (numeric). Route: PUT /product-groups/:groupID/vat-rates/:vatRateID
    mutationFn: ({
      groupId,
      vatRateId,
      payload,
    }: {
      groupId: number;
      vatRateId: number;
      payload: UpdateVatRatePayload;
    }) => dataCentralApi.put(`/product-groups/${groupId}/vat-rates/${vatRateId}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: VAT_RATES_QK }),
  });
}

export function useDeleteVatRate() {
  const qc = useQueryClient();
  return useMutation({
    // groupId = product_group_id (numeric). Route: DELETE /product-groups/:groupID/vat-rates/:vatRateID
    mutationFn: ({ groupId, vatRateId }: { groupId: number; vatRateId: number }) =>
      dataCentralApi.delete(`/product-groups/${groupId}/vat-rates/${vatRateId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: VAT_RATES_QK }),
  });
}
